import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Modal, StatusBadge, formatCurrency, formatDate } from "@/components/pcp";
import { StatusContrato } from "@/pages/Contratos";
import { ItensContrato } from "@/components/contrato/ItensContrato";
import { AditivosContrato } from "@/components/contrato/AditivosContrato";
import { ReajustesContrato } from "@/components/contrato/ReajustesContrato";
import { ReprogramacaoContrato } from "@/components/contrato/ReprogramacaoContrato";
import { GerarOSModal } from "@/components/contrato/GerarOSModal";
import { MedicoesContrato } from "@/components/contrato/MedicoesContrato";
import { PrevisoesContrato } from "@/components/contrato/PrevisoesContrato";
import { ModeloRelatorioFotografico } from "@/components/contrato/ModeloRelatorioFotografico";
import { CheckCircle, Plus, ArrowLeft, Pencil, FilePdf } from "@phosphor-icons/react";
import { Funil } from "@/components/comercial/comercialUtil";
import { usePodeVerValores } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ContratoDetalhe() {
  const vv = usePodeVerValores();
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [gerar, setGerar] = useState(false);
  const [editCab, setEditCab] = useState(null);

  const load = () => api.get(`/contratos/${id}`).then((r) => setC(r.data)).catch(() => { toast.error("Contrato não encontrado"); nav("/contratos"); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!c) return <div className="page-body">Carregando…</div>;
  const isOrc = c.status === "Orçamento";

  const salvarContrato = async (patch) => {
    const { ordens, qtd_medicoes, ...base } = c;
    const payload = { ...base, ...patch, itens: (patch.itens || base.itens || []).map(({ planejado_os, executado, saldo, valor_total, ...i }) => i) };
    await api.put(`/contratos/${id}`, payload);
    await load();
  };

  const aprovar = async () => {
    if (!c.itens?.length) { toast.error("Cadastre ao menos um item antes de aprovar"); return; }
    if (!window.confirm(`Aprovar o orçamento ${c.numero} como Contrato? A partir daí você poderá gerar OS.`)) return;
    await api.post(`/contratos/${id}/aprovar`);
    toast.success("Orçamento aprovado — agora é um Contrato");
    load();
  };

  return (
    <>
      <PageHeader
        testid="page-contrato-detalhe"
        title={<span style={{ display: "flex", gap: 12, alignItems: "center" }}>{c.numero} <StatusContrato status={c.status} /></span>}
        subtitle={`${c.contratante} · ${c.tipo}${c.objeto ? " · " + c.objeto : ""}`}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/contratos" className="btn btn-ghost"><ArrowLeft size={16} weight="bold" /> Voltar</Link>
            {vv && (c.proposta_id ? <Link to={`/propostas/${c.proposta_id}`} className="btn btn-outline" data-testid="btn-proposta"><FilePdf size={16} weight="bold" /> Proposta de origem</Link>
              : <Link to={`/contratos/${id}/proposta`} className="btn btn-outline" data-testid="btn-proposta"><FilePdf size={16} weight="bold" /> Proposta PDF</Link>)}
            <button className="btn btn-outline" onClick={() => setEditCab({ ...c })} data-testid="btn-editar-cabecalho"><Pencil size={16} weight="bold" /> Editar</button>
            {isOrc ? (
              <button className="btn btn-primary" onClick={aprovar} data-testid="btn-aprovar-contrato"><CheckCircle size={16} weight="bold" /> Aprovar como Contrato</button>
            ) : c.status === "Contrato" ? (
              <button className="btn btn-primary" onClick={() => setGerar(true)} data-testid="btn-gerar-os"><Plus size={16} weight="bold" /> Gerar OS</button>
            ) : null}
          </div>
        }
      />
      <div className="page-body">
        {isOrc && <div style={{ padding: "8px 12px", background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 4, fontSize: "0.8rem", marginBottom: 12 }} data-testid="aviso-orcamento-legado">Orçamento legado ainda na base de contratos. Recomendado: executar a separação em <Link to="/migracao-multiempresa" style={{ textDecoration: "underline" }}>Migração</Link> para movê-lo ao módulo Comercial (mesmo ID).</div>}
        {c.origem === "proposta" && <div className="pcp-card" style={{ padding: "10px 16px", marginBottom: 16 }} data-testid="contrato-origem-comercial"><Funil oportunidadeId={c.oportunidade_id} orcamentoId={c.orcamento_id} propostaId={c.proposta_id} contratoId={c.id} contratoNumero={c.numero} /></div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 20 }}>
          {vv && <Kpi label={c.valor_aditivos ? "Valor total (c/ aditivos)" : "Valor total"} value={formatCurrency(c.valor_total_com_aditivos ?? c.valor)} destaque testid="kpi-valor" />}
          <Kpi label="Itens da planilha" value={(c.itens || []).length} />
          <Kpi label="Ordens de serviço" value={c.ordens.length} testid="kpi-os" />
          <Kpi label="Medições fechadas" value={c.qtd_medicoes} />
          {vv && c.qtd_medicoes > 0 && <Kpi label="Faturado PI (medições)" value={formatCurrency(c.valor_faturado_pi)} testid="kpi-faturado-pi" />}
          {vv && ((c.reajustes || []).length > 0 || c.valor_reajuste_acumulado > 0) && <Kpi label="Reajuste R acumulado" value={formatCurrency(c.valor_reajuste_acumulado)} testid="kpi-reajuste-r" />}
          {vv && ((c.reajustes || []).length > 0 || c.valor_reajuste_acumulado > 0) && <Kpi label="Total reajustado (PI + R)" value={formatCurrency(c.valor_total_reajustado)} destaque testid="kpi-total-reajustado" />}
          <Kpi label="Vigência" value={`${formatDate(c.data_inicio)} → ${formatDate(c.data_fim)}`} small />
        </div>

        <ItensContrato contrato={c} onSave={(itens) => salvarContrato({ itens })} onReload={load} />
        {!isOrc && vv && <AditivosContrato contrato={c} onReload={load} />}
        {!isOrc && vv && <ReajustesContrato contrato={c} />}
        {!isOrc && vv && <ReprogramacaoContrato contrato={c} onReload={load} />}

        {!isOrc && (
          <div className="pcp-card" style={{ padding: 16, marginTop: 20 }} data-testid="os-do-contrato">
            <div className="font-display font-bold" style={{ fontSize: "1rem", marginBottom: 10 }}>Ordens de serviço deste contrato ({c.ordens.length})</div>
            {c.ordens.length === 0 ? <div style={{ color: "#71717A", fontSize: "0.85rem" }}>Nenhuma OS gerada. Use "Gerar OS" para criar a primeira a partir da planilha.</div> : (
              <table className="pcp-table">
                <thead><tr><th>Número</th><th>Status</th><th>Rodovia / Trecho</th><th>Município</th><th>Prazo</th><th>Itens na planilha</th><th style={{ textAlign: "right" }}>Avanço</th></tr></thead>
                <tbody>
                  {c.ordens.map((o) => (
                    <tr key={o.id} onClick={() => nav(`/ordens/${o.id}`)} style={{ cursor: "pointer" }} data-testid={`os-row-${o.id}`}>
                      <td className="font-mono font-semibold">{o.numero}</td>
                      <td><StatusBadge status={o.status} /></td>
                      <td>{o.rodovia}{o.trecho ? ` · ${o.trecho}` : ""}</td>
                      <td>{o.municipio}</td>
                      <td className="font-mono text-xs">{formatDate(o.prazo_conclusao)}</td>
                      <td data-testid={`os-vinc-${o.id}`}>
                        {o.itens_total === 0 ? <span style={{ color: "#71717A", fontSize: "0.75rem" }}>sem itens</span>
                          : o.itens_vinculados === o.itens_total ? <span style={{ color: "#047857", fontSize: "0.75rem", fontWeight: 600 }}>✓ {o.itens_vinculados}/{o.itens_total}</span>
                          : <span style={{ color: "#B45309", fontSize: "0.75rem", fontWeight: 700 }} title="Abra a OS e use 'Vincular a contrato' para casar os itens">⚠ {o.itens_vinculados}/{o.itens_total} — não entram na medição</span>}
                      </td>
                      <td className="font-mono" style={{ textAlign: "right" }}>{Math.round(o.avanco || 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!isOrc && vv && <PrevisoesContrato contrato={c} />}
        {!isOrc && <ModeloRelatorioFotografico contrato={c} onChange={load} />}
        {!isOrc && <MedicoesContrato contrato={c} onChange={load} />}
      </div>

      <GerarOSModal open={gerar} onClose={() => setGerar(false)} contrato={c} onCreated={(os) => { setGerar(false); toast.success(`OS ${os.numero} gerada`); nav(`/ordens/${os.id}`); }} />

      <Modal open={!!editCab} onClose={() => setEditCab(null)} title="Editar dados do contrato">
        {editCab && (
          <div style={{ display: "grid", gap: 12 }}>
            <div className="label">Número</div><input className="input" value={editCab.numero} onChange={(e) => setEditCab({ ...editCab, numero: e.target.value })} />
            <div className="label">Contratante</div><input className="input" value={editCab.contratante} onChange={(e) => setEditCab({ ...editCab, contratante: e.target.value })} />
            <div className="label">Objeto</div><textarea className="textarea" rows={2} value={editCab.objeto || ""} onChange={(e) => setEditCab({ ...editCab, objeto: e.target.value })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div><div className="label">Status</div><select className="select" value={editCab.status || "Contrato"} onChange={(e) => setEditCab({ ...editCab, status: e.target.value })} data-testid="edit-status">{editCab.status === "Orçamento" && <option>Orçamento</option>}<option>Contrato</option><option>Encerrado</option></select></div>
              <div><div className="label">Início</div><input type="date" className="input" value={editCab.data_inicio || ""} onChange={(e) => setEditCab({ ...editCab, data_inicio: e.target.value })} /></div>
              <div><div className="label">Fim</div><input type="date" className="input" value={editCab.data_fim || ""} onChange={(e) => setEditCab({ ...editCab, data_fim: e.target.value })} /></div>
            </div>
            <div className="label">Observações</div><textarea className="textarea" rows={2} value={editCab.observacoes || ""} onChange={(e) => setEditCab({ ...editCab, observacoes: e.target.value })} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setEditCab(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={async () => { const { ordens, qtd_medicoes, ...p } = editCab; await salvarContrato(p); setEditCab(null); toast.success("Contrato atualizado"); }} data-testid="btn-salvar-cabecalho">Salvar</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function Kpi({ label, value, destaque, small, testid }) {
  return (
    <div className="pcp-card" style={{ padding: 12, background: destaque ? "#FFFBEA" : undefined, borderColor: destaque ? "#FFCC00" : undefined }} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={small ? "font-mono" : "font-mono font-semibold"} style={{ fontSize: small ? "0.85rem" : "1.3rem" }}>{value}</div>
    </div>
  );
}
